import customLocalStorage, { customSessionStorage } from './storage';

function convertPrice(amount: number) {
  const currencyConfiguration = getCurrency();
  const moneyFormatFunction = (price: number | undefined) => {
    if (typeof price === 'undefined') return amount.toString();
    const convertedAmount = currencyConfiguration.convert
      ? currencyConfiguration.convert(price)
      : price;
    return currencyConfiguration.format
      ? currencyConfiguration.format(convertedAmount)
      : price.toString();
  };

  if (typeof window.CANDYRACK_CUSTOM_FORMAT_MONEY !== 'function') {
    window.CANDYRACK_CUSTOM_FORMAT_MONEY = moneyFormatFunction;
  }
  if (typeof window.CANDYCART_CUSTOM_FORMAT_MONEY !== 'function') {
    window.CANDYCART_CUSTOM_FORMAT_MONEY = moneyFormatFunction;
  }

  return amount;
}

interface CurrencyConfiguration {
  convert?: (amount: number) => number;
  format?: (amount: number) => string;
  currency: string;
}

function getCurrency(): CurrencyConfiguration {
  const storeCurrency = window.Shopify?.currency.active;

  // temp fix for https://github.com/digismoothie/candyrack/pull/662 -- https://digismoothie.atlassian.net/browse/APPS-2132
  // by doing this there is an assumption we are on admin and not client in reality
  if (!window.CandyRack && !window.CandyCart && !window.LastUpsell) {
    return {
      convert: (amount) => amount,
      format: (amount) => formatMoney(amount * 100),
      currency: storeCurrency,
    };
  }

  if (window.CandyRack?.currency?.configuration) {
    return window.CandyRack.currency.configuration;
  }

  if (window.LastUpsell?.currency?.configuration) {
    return window.LastUpsell.currency.configuration;
  }

  if (window.DoublyGlobalCurrency) {
    return {
      convert: (amount) =>
        window.DoublyGlobalCurrency.convert(
          amount * 100,
          storeCurrency,
          window.DoublyGlobalCurrency.currentCurrency,
        ),
      format: (amount) =>
        window.DoublyGlobalCurrency.formatMoney(
          amount,
          window.DoublyGlobalCurrency.moneyFormats[
            window.DoublyGlobalCurrency.currentCurrency
          ][window.DoublyGlobalCurrency.format],
          true,
          false,
        ),
      currency: window.DoublyGlobalCurrency.currentCurrency,
    };
  }

  if (window.ACSCurrency && window.ACSCurrency.currentCurrency) {
    const convertFn = (amount: number) =>
      window.ACSCurrency.convert && window.ACSCurrency.currentCurrency
        ? window.ACSCurrency.convert(
            amount * 100,
            storeCurrency,
            window.ACSCurrency.currentCurrency,
          )
        : amount;
    const formatFn = (amount: number) =>
      window.ACSCurrency.formatMoney &&
      window.ACSCurrency.moneyFormats &&
      window.ACSCurrency.currentCurrency
        ? window.ACSCurrency.formatMoney(
            amount,
            window.ACSCurrency.moneyFormats[window.ACSCurrency.currentCurrency][
              window.ACSCurrency.format
            ],
          )
        : formatMoneyIntl(amount, storeCurrency);
    return {
      convert: convertFn,
      format: formatFn,
      currency: window.ACSCurrency.currentCurrency,
    };
  }

  const vitalsCurrency = customLocalStorage.getItem('__v_cc__s_c__');
  if (vitalsCurrency && vitalsCurrency.length === 3) {
    const convertFn = (amount: number) =>
      window.Currency && window.Currency.convert
        ? window.Currency.convert(amount, storeCurrency, vitalsCurrency)
        : amount;
    return {
      convert: convertFn,
      format: (amount) => formatMoneyIntl(amount, vitalsCurrency),
      currency: vitalsCurrency,
    };
  }

  if (window.BOLDCURRENCY && window.BOLDCURRENCY.enabled) {
    return {
      convert: (amount) =>
        window.BOLDCURRENCY.converter.convertPrice(
          amount * 100,
          window.BOLDCURRENCY.currentCurrency,
        ),
      format: (amount) => window.BOLDCURRENCY.converter.formatMoney(amount),
      currency: window.BOLDCURRENCY.currentCurrency,
    };
  }

  if (window.conversionBearAutoCurrencyConverter) {
    return {
      format: (amount) => {
        const { amount: convertedAmount, default_format } =
          window.conversionBearAutoCurrencyConverter!.convert(amount);
        return default_format.replace(/\{{[^)]*\}}/g, convertedAmount);
      },
      currency: window.conversionBearAutoCurrencyConverter.currency,
    };
  }

  if (
    window.LangShop &&
    typeof window.LangShop.getCurrencyCode === 'function' &&
    typeof window.LangShop.langShop.currencyConverter === 'function'
  ) {
    return {
      convert: (amount) =>
        window.LangShop!.langShop.currencyConverter.convert(
          amount * 100,
          storeCurrency.toLowerCase(),
          window.LangShop!.getCurrencyCode().toLowerCase(),
        ),
      format: (amount) => window.LangShop!.formatMoney(amount),
      currency: window.LangShop.getCurrencyCode(),
    };
  }

  let baCurrency = customSessionStorage.getItem('bacurr_user_cur') || '';
  baCurrency = baCurrency.slice(1, -1);
  if (
    window.baCurr &&
    baCurrency.length === 3 &&
    typeof window.baCurr.widgetConv === 'function'
  ) {
    return {
      format: (amount) => window.baCurr!.widgetConv(amount, baCurrency),
      currency: baCurrency,
    };
  }

  const autoketingCurrency = customLocalStorage.getItem(
    'autoketing-currency-location-customer-v1',
  );
  if (autoketingCurrency && autoketingCurrency.length === 3) {
    return {
      format: (amount) => {
        let formattedAmount = amount.toString();
        applyDOMObservingFormatter(amount, 'span', 'money').then(
          (result) => (formattedAmount = result),
        );
        return formattedAmount;
      },
      currency: autoketingCurrency,
    };
  }

  if (
    window.SECOMAPP &&
    window.SECOMAPP.cookie &&
    typeof window.SECOMAPP.cookie === 'function'
  ) {
    const getMoneyFormat = () => {
      if (
        window.Currency &&
        window.Currency.moneyFormats &&
        window.Currency.format &&
        window.Currency.currentCurrency
      ) {
        return window.Currency.moneyFormats[window.Currency.currentCurrency][
          window.Currency.format
        ];
      }
      if (window.theme?.moneyFormat) {
        return window.theme.moneyFormat;
      }

      /* When window.SECOMAPP was introduced, it seems
       ** it was relevant for the CR (add to cart) situation.
       ** Given how our code has evolved, it make sense
       ** to also check for LU (thank you page) - though
       ** it could be irrelevant for our case.
       */
      return getMoneyFormatFromWindow();
    };
    const moneyFormat = getMoneyFormat();
    let formatFn = (amount: number) => formatMoneyIntl(amount, storeCurrency);
    if (window.Currency?.formatMoney && moneyFormat) {
      formatFn = (amount: number) => {
        const cents = window.Currency!.convert(
          amount,
          window.Currency!.shopCurrency,
          window.Currency!.currentCurrency,
        );
        return window.Currency!.formatMoney(cents, moneyFormat);
      };
    }
    if (window.Shopify.formatMoney && moneyFormat)
      formatFn = (amount) =>
        window.Shopify.formatMoney(amount, moneyFormat, false);
    if (window.Shopify.api && window.Shopify.api.formatMoney && moneyFormat)
      formatFn = (amount) =>
        window.Shopify.api.formatMoney(amount, moneyFormat, false);
    if (window.theme && window.theme.Currency && moneyFormat)
      formatFn = (amount) =>
        window.theme!.Currency!.formatMoney(amount, moneyFormat, false);
    return {
      format: (amount) => formatFn(amount * 100),
      currency: window.Currency?.currentCurrency || storeCurrency,
    };
  }

  return {
    convert: (amount) => amount,
    format: (amount) => formatMoney(amount * 100),
    currency: storeCurrency,
  };
}

function applyDOMObservingFormatter(
  cents: number,
  element: string,
  className: string,
) {
  const toConvert = window.document.createElement(element);
  toConvert.classList.add(className);
  toConvert.innerHTML = cents.toString();
  window.document.body.append(toConvert);
  return new Promise<string>((resolve, reject) => {
    new MutationObserver((_, observer) => {
      resolve(toConvert.innerHTML);
      observer.disconnect();
    }).observe(toConvert, { childList: true, subtree: true });
  });
}

function formatMoneyIntl(cents: number, currency?: string) {
  const limitedAmount = cents < 0 ? 0 : cents;

  if (window.Intl && window.navigator) {
    // An undefined currency would lead to an error
    // Its basically not that good, but its better then silently handling it,
    // and its how this is defined for a long period of time.
    return new Intl.NumberFormat(window.navigator.language, {
      style: 'currency',
      currency,
    }).format(limitedAmount);
  }

  return `${limitedAmount} ${currency}`;
}

function formatWithDelimiters(
  number: number,
  precision = 2,
  thousands = ',',
  decimal = '.',
) {
  if (isNaN(number) || number == null) {
    return '0';
  }

  const formattedNumber = (number / 100.0).toFixed(precision);

  const parts = formattedNumber.split('.');
  const dollarsAmount = parts[0].replace(
    /(\d)(?=(\d\d\d)+(?!\d))/g,
    `$1${thousands}`,
  );
  const centsAmount = parts[1] ? decimal + parts[1] : '';

  return dollarsAmount + centsAmount;
}

function getMoneyFormatFromWindow(): string | undefined {
  if (
    window.CandyRack?.currency?.moneyFormats &&
    window.CandyRack?.currency?.format
  ) {
    return window.CandyRack!.currency!.moneyFormats[
      window.CandyRack!.currency!.format
    ];
  }
  if (
    window.LastUpsell?.currency?.moneyFormats &&
    window.LastUpsell?.currency?.format
  ) {
    return window.LastUpsell!.currency!.moneyFormats[
      window.LastUpsell!.currency!.format
    ];
  }
  return undefined;
}

function formatMoney(cents: number) {
  let value = '';
  const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;

  if (
    (!window.CandyRack?.currency || window.CandyRack?.currency.defaultFormat) &&
    !window.LastUpsell?.currency
  ) {
    return formatMoneyIntl(cents / 100, window.Shopify?.currency.active);
  }

  const formatString: string | undefined = getMoneyFormatFromWindow();
  const match = formatString?.match(placeholderRegex);
  if (!formatString || !match) return formatWithDelimiters(cents);

  switch (match[1]) {
    case 'amount':
      value = formatWithDelimiters(cents, 2);
      break;
    case 'amount_no_decimals':
      value = formatWithDelimiters(cents, 0);
      break;
    case 'amount_with_comma_separator':
      value = formatWithDelimiters(cents, 2, '.', ',');
      break;
    case 'amount_no_decimals_with_comma_separator':
      value = formatWithDelimiters(cents, 0, '.', ',');
      break;
  }

  /*
   ** In case we receive unsupported money format from BE (taken from theme settings)
   ** we need to use a default formatting as it would result in empty price in the popup
   */
  if (!value) {
    return formatMoneyIntl(cents / 100, window.Shopify?.currency.active);
  }

  return formatString.replace(placeholderRegex, value);
}

/*
 ** It seems that we don't always have window.Shopify.checkout information available
 ** when we expect it. Using this approach we can give it some time to load.
 ** As we are not sure on why the checkout object remains empty we can't be sure
 ** that this fix will improve the situation. Its worth however trying it out.
 **
 ** Please not that window.Shopify.checkout is a different object from window.Shopify.Checkout
 */
function waitForShopifyCheckoutProperty(): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    const intervalId = setInterval(() => {
      if (window.Shopify && window.Shopify.checkout) {
        clearInterval(intervalId);
        resolve();
      }
    }, 100);

    setTimeout(() => {
      clearInterval(intervalId);
      reject(
        new Error('Timeout waiting for Shopify.checkout to become available'),
      );
    }, 3000);
  });
}

export {
  getCurrency,
  convertPrice,
  formatMoney,
  waitForShopifyCheckoutProperty,
  getMoneyFormatFromWindow,
};
